// eslint-disable-next-line no-restricted-imports
import {useState} from 'react';
import dynamic from 'next/dynamic';
import mixpanel from 'mixpanel-browser';
import {ResetPasswordForm, SubmitEmailForm} from './formValidationSchema';
import {useCognito, useCognitoVerification} from '../../hooks/cognito';
import {useAppToast} from '../../hooks/app-toast';
import {EmailEntryProps} from './reset-password-email-entry';
import {PasswordCodeProps} from './reset-password-code-form';
import {ResetPasswordProps} from './reset-password-form';

const SubmitEmail = dynamic<EmailEntryProps>(() =>
  import('./reset-password-email-entry').then((mod) => mod.EmailEntrySection),
);
const PasswordCode = dynamic<PasswordCodeProps>(() =>
  import('./reset-password-code-form').then((mod) => mod.PasswordCode),
);
const ResetPassword = dynamic<ResetPasswordProps>(() =>
  import('./reset-password-form').then((mod) => mod.ResetPasswordSection),
);

interface ResetPasswordControllerProps {
  trackingNamePrefix?: string;
  onPasswordReset?: () => void;
  maxSteps?: number;
}

// eslint-disable-next-line no-shadow
enum FORMS {
  SUBMIT_EMAIL = 'submit-email',
  CONFIRMATION_CODE = 'confirmation-code',
  SUBMIT_NEW_PASSWORD = 'submit-new-password',
}

export const ResetPasswordController: React.FC<
  ResetPasswordControllerProps
> = ({trackingNamePrefix, maxSteps = 3, onPasswordReset}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState<FORMS>(FORMS.SUBMIT_EMAIL);
  const [accountEmail, setAccountEmail] = useState<SubmitEmailForm>();
  const [verificationCode, setVerificationCode] = useState<string>('');
  const cognito = useCognito();
  const verification = useCognitoVerification();
  const toast = useAppToast();

  const emailEntrySubmit = async (props: SubmitEmailForm) => {
    setIsLoading(true);
    setAccountEmail(props);
    try {
      await cognito.forgotPassword(props.email).then(() => {
        toast.showInfoToast(
          'A password reset code has been sent to your verified contact method (phone or email)',
        );
        setIsLoading(false);
        setFormState(FORMS.CONFIRMATION_CODE);
        mixpanel.track(`${trackingNamePrefix} 1/${maxSteps}`);
      });
    } catch (e: any) {
      setIsLoading(false);
      if (e.name === 'CodeDeliveryFailureException') {
        toast.showErrorToast(
          'Failed to send verification code. Please try again.',
        );
      } else if (e.name === 'UserNotFoundException') {
        toast.showErrorToast(
          'We were unable to send a verification code. If you have previously created an account using Google Sign-In then navigate to Login to use your credentials.',
        );
      } else {
        toast.showErrorToast(
          'Failed to send verification code. Please try again or contact support.',
        );
        throw e;
      }
    }
  };

  const passwordCodeSubmit = (code: string) => {
    setVerificationCode(code);
    setFormState(FORMS.SUBMIT_NEW_PASSWORD);
    mixpanel.track(`${trackingNamePrefix} 2/${maxSteps}`);
  };

  const resetPasswordSubmit = async (props: ResetPasswordForm) => {
    setIsLoading(true);
    if (accountEmail) {
      try {
        await cognito
          .forgotPasswordSubmit(
            accountEmail.email,
            verificationCode,
            props.password,
          )
          .then(() => {
            toast.showInfoToast('Password reset successfully');
            mixpanel.track(`${trackingNamePrefix} 3/${maxSteps}`);
            if (onPasswordReset) onPasswordReset();
          });
      } catch (e: any) {
        setIsLoading(false);
        if (e.name === 'CodeMismatchException') {
          toast.showErrorToast(
            'The code provided does not match our records. Please try again.',
          );
          setFormState(FORMS.CONFIRMATION_CODE);
        } else if (e.name === 'ExpiredCodeException') {
          toast.showErrorToast(
            'The code provided has expired. Please try again.',
          );
          setFormState(FORMS.SUBMIT_EMAIL);
        } else if (e.name === 'InvalidPasswordException') {
          toast.showErrorToast(
            'The password you submitted was not valid. Please try again.',
          );
        } else {
          throw e;
        }
      }
    }
  };

  const handleResendConfirmation = () => {
    verification.onResendVerificationEmailOrSms(accountEmail?.email);
  };

  switch (formState) {
    case FORMS.SUBMIT_EMAIL:
      return (
        <>
          <SubmitEmail
            onNextSubmit={emailEntrySubmit}
            maxStepCount={maxSteps}
            isLoading={isLoading}
          />
        </>
      );
    case FORMS.CONFIRMATION_CODE:
      return (
        <>
          <PasswordCode
            onNextSubmit={passwordCodeSubmit}
            maxStepCount={maxSteps}
            isLoading={isLoading}
            onResend={handleResendConfirmation}
          />
        </>
      );

    case FORMS.SUBMIT_NEW_PASSWORD:
      return (
        <>
          <ResetPassword
            onNextSubmit={resetPasswordSubmit}
            maxStepCount={maxSteps}
            isLoading={isLoading}
          />
        </>
      );
    default:
      return <></>;
  }
};
